import React, { createContext, useContext } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { MeResponse, LoginInput, RegisterTrainerInput } from "@gymplan/shared";
import { api, ApiRequestError } from "./api.ts";

interface AuthCtx {
  user: MeResponse | null;
  loading: boolean;
  login: (i: LoginInput) => Promise<void>;
  register: (i: RegisterTrainerInput) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.get<MeResponse>("/api/auth/me");
      } catch (e) {
        if (e instanceof ApiRequestError && e.status === 401) return null;
        throw e;
      }
    },
  });

  const value: AuthCtx = {
    user: me.data ?? null,
    loading: me.isLoading,
    login: async (i) => {
      const u = await api.post<MeResponse>("/api/auth/login", i);
      qc.setQueryData(["me"], u);
    },
    register: async (i) => {
      const u = await api.post<MeResponse>("/api/auth/register", i);
      qc.setQueryData(["me"], u);
    },
    logout: async () => {
      await api.post("/api/auth/logout");
      qc.setQueryData(["me"], null);
      qc.clear();
    },
    refresh: async () => {
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth outside AuthProvider");
  return c;
}
